import React, { useState, useEffect } from 'react';
import {
    ChevronLeft,
    ChevronUp,
    ChevronDown,
    Search,
    Loader2,
    User,
    AlertCircle,
    Home,
    RefreshCw,
    Shuffle,
    CheckCircle
} from 'lucide-react';
import marketingService from '../services/marketingService';
import Nt from './Nt';

/**
 * Компонент дерева Light Mix
 * Props:
 * - drawId: ID розыгрыша (обязательный)
 * - onBack: callback для возврата назад
 */
const LightMixTree = ({ drawId, onBack }) => {
    // Тарифы и уровни
    const [tarifs, setTarifs] = useState([]);
    const [levels, setLevels] = useState([]);
    const [selectedTarif, setSelectedTarif] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [loadingLevels, setLoadingLevels] = useState(false);

    // Дерево
    const [treeData, setTreeData] = useState(null);
    const [maxPositions, setMaxPositions] = useState(3);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Поиск
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // 1. Загрузка при монтировании
    useEffect(() => {
        if (drawId) {
            initLoad();
        }
    }, [drawId]);

    // 2. При смене тарифа - загружаем уровни
    useEffect(() => {
        if (selectedTarif && drawId && tarifs.length > 0) {
            loadLevels(selectedTarif);
        }
    }, [selectedTarif]);

    // 3. При смене уровня - загружаем дерево
    useEffect(() => {
        if (selectedLevel && selectedTarif && drawId && levels.length > 0) {
            loadTree(null);
        }
    }, [selectedLevel]);

    // Начальная загрузка
    const initLoad = async () => {
        setLoading(true);
        setError(null);
        try {
            // Загружаем тарифы (без draw_id)
            const tarifsRes = await marketingService.getLightMixTarifs();
            const loadedTarifs = tarifsRes.success && tarifsRes.data?.tarifs?.length > 0
                ? tarifsRes.data.tarifs
                : ['mix'];
            setTarifs(loadedTarifs);

            const firstTarif = loadedTarifs[0];
            setSelectedTarif(firstTarif);

            // Загружаем уровни для первого тарифа
            const levelsRes = await marketingService.getLightMixLevels(firstTarif);
            const loadedLevels = levelsRes.success && levelsRes.data?.levels?.length > 0
                ? levelsRes.data.levels.map(l => parseFloat(l))
                : [4];
            setLevels(loadedLevels);

            const firstLevel = loadedLevels[0];
            setSelectedLevel(firstLevel);

            // Загружаем дерево
            const treeRes = await marketingService.getLightMixTreeData(drawId, null, firstTarif, firstLevel);
            if (treeRes.success) {
                setTreeData(treeRes.data.tree);
                setMaxPositions(treeRes.data.max_positions || 3);
            } else {
                setError(treeRes.message || 'Ошибка загрузки дерева');
            }
        } catch (err) {
            console.error('Init load error:', err);
            setError('Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    };

    // Загрузка уровней
    const loadLevels = async (tarif) => {
        setLoadingLevels(true);
        try {
            const response = await marketingService.getLightMixLevels(tarif);
            if (response.success && response.data?.levels?.length > 0) {
                const numericLevels = response.data.levels.map(l => parseFloat(l));
                setLevels(numericLevels);
                setSelectedLevel(numericLevels[0]);
            } else {
                setLevels([4]);
                setSelectedLevel(4);
            }
        } catch (err) {
            console.error('Error loading levels:', err);
            setLevels([4]);
            setSelectedLevel(4);
        } finally {
            setLoadingLevels(false);
        }
    };

    // Загрузка дерева
    const loadTree = async (positionId = null) => {
        setLoading(true);
        setError(null);
        try {
            const response = await marketingService.getLightMixTreeData(
                drawId, positionId, selectedTarif, selectedLevel
            );
            if (response.success) {
                setTreeData(response.data.tree);
                setMaxPositions(response.data.max_positions || 3);
            } else {
                setError(response.message || 'Ошибка');
            }
        } catch (err) {
            setError('Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    };

    // Навигация
    const goDown = (position) => {
        if (position?.id && position.children_count > 0) {
            loadTree(position.id);
        }
    };

    const goUp = () => {
        if (treeData?.mentor_id) {
            loadTree(treeData.mentor_id);
        }
    };

    const goRoot = () => loadTree(null);

    // Поиск
    const handleSearch = async () => {
        if (!searchQuery.trim()) return;
        setIsSearching(true);
        try {
            const response = await marketingService.searchLightMix(
                drawId, searchQuery, selectedTarif, selectedLevel
            );
            if (response.success) {
                setSearchResults(response.data.results || []);
            }
        } catch (err) {
            console.error('Search error:', err);
        } finally {
            setIsSearching(false);
        }
    };

    const goToPosition = (id) => {
        setSearchResults([]);
        setSearchQuery('');
        loadTree(id);
    };

    // Заполнение пустых позиций
    const fillPositions = (children, prefix = '') => {
        const filled = [];
        for (let i = 1; i <= maxPositions; i++) {
            const existing = children?.find(c => c.position === i);
            filled.push(existing || {
                id: `${prefix}empty-${i}`,
                position: i,
                user_id: null,
                children: [],
                children_count: 0
            });
        }
        return filled;
    };

    // Рендер позиции
    const renderPosition = (pos, size = 'normal') => {
        if (!pos) return null;

        const isEmpty = !pos.user_id && pos.user_id !== 0 || pos.id?.toString().startsWith('empty');
        const isTech = pos.is_tech_clone || pos.user_id === 0;
        const hasChildren = (pos.children_count || 0) > 0;
        const isCompleted = pos.status === 'completed';

        const sizeClasses = size === 'large'
            ? 'w-28 h-28'
            : size === 'small'
                ? 'w-14 h-14'
                : 'w-20 h-20';

        const avatarSize = size === 'large' ? 'w-14 h-14' : size === 'small' ? 'w-8 h-8' : 'w-10 h-10';
        const textSize = size === 'small' ? 'text-xs' : 'text-sm';

        return (
            <div className="flex flex-col items-center">
                <div
                    onClick={() => hasChildren && !isEmpty && goDown(pos)}
                    className={`
                        ${sizeClasses} rounded-xl border-2 flex flex-col items-center justify-center relative transition-all
                        ${isEmpty
                        ? 'bg-gray-50 border-dashed border-gray-300'
                        : isTech
                            ? 'bg-gray-100 border-gray-300'
                            : hasChildren
                                ? 'bg-white border-purple-300 hover:border-purple-500 hover:shadow-lg cursor-pointer'
                                : 'bg-white border-purple-200'
                    }
                    `}
                >
                    {/* Индикатор completed */}
                    {isCompleted && !isEmpty && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle size={12} className="text-white" />
                        </div>
                    )}

                    {/* Аватар */}
                    <div className={`${avatarSize} rounded-full flex items-center justify-center mb-1 ${
                        isEmpty ? 'bg-gray-200' : isTech ? 'bg-gray-300' : 'bg-gradient-to-br from-purple-400 to-pink-400'
                    }`}>
                        {isEmpty ? (
                            <User size={size === 'small' ? 14 : 18} className="text-gray-400" />
                        ) : isTech ? (
                            <Shuffle size={size === 'small' ? 14 : 18} className="text-gray-500" />
                        ) : pos.avatar ? (
                            <img src={pos.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                            <span className="text-white font-bold text-xs">
                                {pos.login?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                        )}
                    </div>

                    {/* Логин */}
                    <div className={`${textSize} font-medium text-center truncate max-w-full px-1 ${
                        isEmpty ? 'text-gray-400' : 'text-gray-700'
                    }`}>
                        <Nt>{isEmpty ? '—' : isTech ? 'Тех' : (pos.login?.slice(0, 8) || '—')}</Nt>
                    </div>

                    {/* Счётчик детей */}
                    {!isEmpty && hasChildren && size !== 'small' && (
                        <div className="text-xs text-purple-500 font-medium">
                            {pos.children_count}/{maxPositions}
                        </div>
                    )}
                </div>

                {/* Стрелка вниз */}
                {hasChildren && !isEmpty && size === 'normal' && (
                    <ChevronDown size={16} className="text-green-500 mt-1" />
                )}
            </div>
        );
    };

    // Лоадер
    if (loading && !treeData) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="animate-spin h-12 w-12 text-purple-500" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Шапка */}
            <div className="border-b px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ChevronLeft size={20} />
                    </button>
                    <h3 className="text-lg font-semibold">Структура Light Mix</h3>
                </div>
                <button onClick={() => loadTree(treeData?.id)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <RefreshCw size={18} />
                </button>
            </div>

            {/* Тарифы и уровни */}
            <div className="border-b px-4 py-3 bg-gray-50">
                <div className="flex flex-wrap items-center gap-6">
                    {/* Тарифы */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">Тариф:</span>
                        <div className="flex gap-1">
                            {tarifs.map(t => (
                                <button
                                    key={t}
                                    onClick={() => setSelectedTarif(t)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                        String(selectedTarif).toLowerCase() === String(t).toLowerCase()
                                            ? 'bg-purple-500 text-white'
                                            : 'bg-white border border-gray-300 hover:border-purple-400'
                                    }`}
                                >
                                    {String(t).toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Уровни */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">Уровень:</span>
                        {loadingLevels ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : (
                            <div className="flex gap-1">
                                {levels.map(l => (
                                    <button
                                        key={l}
                                        onClick={() => setSelectedLevel(parseFloat(l))}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                            parseFloat(selectedLevel) === parseFloat(l)
                                                ? 'bg-purple-500 text-white'
                                                : 'bg-white border border-gray-300 hover:border-purple-400'
                                        }`}
                                    >
                                        ${parseFloat(l)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Структура */}
                    <div className="text-sm text-gray-500 ml-auto">
                        Структура: <span className="font-mono">1 → {maxPositions} → {maxPositions * maxPositions}</span>
                    </div>
                </div>
            </div>

            {/* Навигация и поиск */}
            <div className="border-b px-4 py-3 flex flex-wrap items-center gap-3">
                {/* Поиск */}
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Поиск по логину..."
                        className="px-3 py-2 border rounded-lg text-sm w-48"
                    />
                    <button
                        onClick={handleSearch}
                        disabled={isSearching}
                        className="px-3 py-2 bg-purple-500 text-white rounded-lg text-sm"
                    >
                        {isSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                    </button>
                </div>

                {/* Кнопки навигации */}
                <div className="flex items-center gap-2 ml-auto">
                    <button
                        onClick={goRoot}
                        className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                    >
                        <Home size={16} />
                        К вершине
                    </button>

                    {treeData?.mentor_id && (
                        <button
                            onClick={goUp}
                            className="flex items-center gap-1 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm"
                        >
                            <ChevronUp size={16} />
                            Вверх
                        </button>
                    )}
                </div>
            </div>

            {/* Результаты поиска */}
            {searchResults.length > 0 && (
                <div className="px-4 py-2 bg-purple-50 border-b">
                    <div className="flex flex-wrap gap-2">
                        {searchResults.map(r => (
                            <button
                                key={r.id}
                                onClick={() => goToPosition(r.id)}
                                className="px-3 py-1 bg-white border border-purple-300 rounded-lg text-sm hover:bg-purple-100"
                            >
                                <Nt>{r.login}</Nt> (#{r.position})
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ДЕРЕВО */}
            <div className="p-6 overflow-x-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="animate-spin h-8 w-8 text-purple-500" />
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
                        <p className="text-red-600">{error}</p>
                    </div>
                ) : treeData ? (
                    <div className="flex flex-col items-center min-w-fit">

                        {/* Индикатор что мы не в корне */}
                        {treeData.mentor_id && (
                            <div className="mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                                Просмотр ветки • <button onClick={goRoot} className="underline">Вернуться к вершине</button>
                            </div>
                        )}

                        {/* УРОВЕНЬ 1: Текущая позиция */}
                        <div className="mb-6">
                            {renderPosition(treeData, 'large')}

                            {treeData.mentor_id && (
                                <button
                                    onClick={goUp}
                                    className="mt-2 mx-auto flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                                >
                                    <ChevronUp size={12} /> К ментору
                                </button>
                            )}
                        </div>

                        {/* Линия вниз */}
                        <div className="h-8 w-0.5 bg-purple-300"></div>

                        {/* Горизонтальная линия */}
                        <div
                            className="h-0.5 bg-purple-300"
                            style={{ width: `${maxPositions * 100}px` }}
                        ></div>

                        {/* УРОВЕНЬ 2: Дети */}
                        <div className="grid gap-6 mt-4" style={{ gridTemplateColumns: `repeat(${maxPositions}, minmax(0, 1fr))` }}>
                            {fillPositions(treeData.children, 'child-').map((child, idx) => (
                                <div key={child.id || idx} className="flex flex-col items-center">
                                    {/* Вертикальная линия */}
                                    <div className="h-4 w-0.5 bg-purple-300 -mt-4"></div>

                                    {renderPosition(child)}

                                    {/* УРОВЕНЬ 3: Внуки */}
                                    {child.children && child.children.length > 0 && (
                                        <>
                                            <div className="h-4 w-0.5 bg-purple-200 mt-2"></div>
                                            <div className="flex gap-1 mt-1">
                                                {fillPositions(child.children, `gc-${idx}-`).map((gc, gcIdx) => {
                                                    const isEmpty = gc.id?.toString().includes('empty') || (gc.user_id === null || gc.user_id === undefined);
                                                    const isTechClone = gc.is_tech_clone || gc.user_id === 0;
                                                    const hasUser = !isEmpty && !isTechClone;

                                                    return (
                                                        <div
                                                            key={gc.id || gcIdx}
                                                            onClick={() => gc.children_count > 0 && goDown(gc)}
                                                            className={`
                                                                w-10 h-10 rounded-lg border flex items-center justify-center
                                                                text-xs font-medium transition-all relative
                                                                ${isEmpty
                                                                ? 'bg-gray-50 border-dashed border-gray-300 text-gray-400'
                                                                : isTechClone
                                                                    ? 'bg-gray-200 border-gray-400 text-gray-600'
                                                                    : gc.children_count > 0
                                                                        ? 'bg-purple-100 border-purple-300 text-purple-700 cursor-pointer hover:bg-purple-200'
                                                                        : 'bg-purple-50 border-purple-200 text-purple-600'
                                                            }
                                                            `}
                                                            title={isTechClone ? 'Тех. клон' : (gc.login || 'Свободно')}
                                                        >
                                                            {gc.status === 'completed' && !isEmpty && (
                                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></div>
                                                            )}
                                                            {isEmpty
                                                                ? '—'
                                                                : isTechClone
                                                                    ? <Shuffle size={14} className="text-gray-500" />
                                                                    : gc.login?.charAt(0)?.toUpperCase() || '?'
                                                            }
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12 text-gray-500">
                        Дерево пусто
                    </div>
                )}
            </div>

            {/* Легенда */}
            <div className="border-t px-4 py-3 bg-gray-50 text-xs text-gray-500 flex justify-center gap-6">
                <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-purple-400"></div> Участник
                </span>
                <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div> Закрыт
                </span>
                <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div> Тех. клон
                </span>
                <span className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded border border-dashed border-gray-400"></div> Свободно
                </span>
            </div>
        </div>
    );
};

export default LightMixTree;
